import { useQuery } from '@apollo/client';
import { useDialog, useLogEvent } from 'components/hooks';
import useSiteMutations from 'components/Sites/hooks/useSiteMutations';
import { appQueryParams } from 'constants/appQueryParams';
import { SITES_TABLE_ROWS_PER_PAGE } from 'constants/pagination';
import { GET_BASE_PARTNER_COMPANY } from 'graphql/queries/partner.queries';
import { GET_SITES } from 'graphql/queries/site.queries';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { IBasePartnerCompany } from 'types/partner.types';
import { RouteParamsWithId } from 'types/router.types';
import { IClusterSite, ISite, SiteType, SiteViewOption } from 'types/site.types';
import { GraphQlConnection } from 'types/types';
import { StringParam, createEnumParam, useQueryParams, withDefault } from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

const usePartnerSites = () => {
  const { id } = useParams<RouteParamsWithId>();
  const [query, setQuery] = useQueryParams(
    {
      [appQueryParams.query]: withDefault(StringParam, ''),
      [appQueryParams.viewOption]: withDefault(
        createEnumParam<SiteViewOption>([SiteViewOption.CLUSTER, SiteViewOption.LIST]),
        SiteViewOption.CLUSTER
      ),
    },
    { removeDefaultsFromUrl: true }
  );

  const searchTerm = query[appQueryParams.query];
  const siteViewOption = query[appQueryParams.viewOption];

  const { logEvent } = useLogEvent();
  const { openDialog } = useDialog();
  const { deleteSite } = useSiteMutations();

  const { data, loading, error, fetchMore, refetch } = useQuery<{
    sites: GraphQlConnection<ISite>;
  }>(GET_SITES, {
    variables: {
      filters: {
        // We have to send null to get all sites since the default is INTERNAL
        ownership: null,
        ownedById: id,
        ...(searchTerm ? { searchTerm: searchTerm } : {}),
      },
      siteViewOption: siteViewOption,
      first: SITES_TABLE_ROWS_PER_PAGE,
    },
  });
  const { data: partnerData } = useQuery<{ partner: IBasePartnerCompany }>(
    GET_BASE_PARTNER_COMPANY,
    {
      variables: {
        id,
      },
    }
  );

  const partner = partnerData?.partner;

  const sites: ISite[] = useMemo(
    () => (data?.sites ? removeGraphConnections(data.sites) : []),
    [data]
  );

  const hasNextPage: boolean = !!data?.sites?.pageInfo?.hasNextPage;

  const endCursor: string | undefined = data?.sites?.pageInfo?.endCursor;

  const handlePageEndReached = () => {
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  useEffect(() => {
    if (!sites?.length && hasNextPage) {
      handlePageEndReached();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, hasNextPage]);

  const handleManageClusterSites = (siteClusterId: string) => {
    const parentSiteCluster = sites.find(site => site.id === siteClusterId);
    if (parentSiteCluster?.siteType !== SiteType.CLUSTER) {
      throw new Error('This dialog can only be used for cluster sites');
    }
    openDialog({
      type: 'ADD_MANAGE_CLUSTER_DECISION',
      props: { siteCluster: parentSiteCluster, partner: parentSiteCluster?.ownedBy },
    });
  };

  const handleCreateSite = () => {
    openDialog({
      type: 'ADD_EDIT_SITE',
      // In the client view internal sites we prefill the company with your own company
      props: {
        partner,
        hidePartnerSelector: true,
      },
    });
  };

  const handleEditSite = (site: ISite) => {
    openDialog({
      type: 'ADD_EDIT_SITE',
      props: {
        site,
        hidePartnerSelector: true,
      },
    });
  };

  const handleCreateSiteCluster = () => {
    openDialog({
      type: 'ADD_EDIT_SITE_CLUSTER',
      props: {
        partner,
        hidePartnerSelector: true,
      },
    });
  };

  const handleEditSiteCluster = (siteCluster: IClusterSite) => {
    openDialog({
      type: 'ADD_EDIT_SITE_CLUSTER',
      props: {
        siteCluster,
        hidePartnerSelector: true,
      },
    });
  };

  const handleDeleteSite = (siteId: string) => {
    const siteTitle = sites.find(site => site.id === siteId)?.title;
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete site',
        text: 'Are you sure you want to delete this site? All data will be lost and you will not be able to recover this item.',
        submitText: 'Delete',
        itemTitle: siteTitle,
        displayCloseButton: true,
        onSubmit: () => {
          deleteSite({ variables: { id: siteId } });
        },
        onCancel: () => undefined,
      },
    });
  };

  const handleDeleteSiteCluster = (siteId: string) => {
    const site = sites.find(site => site.id === siteId);
    if (site?.siteType !== SiteType.CLUSTER) {
      throw new Error('This dialog can only be used for cluster');
    }
    openDialog({
      type: 'DELETE_SITE_CLUSTER',
      props: {
        siteCluster: site,
      },
    });
  };

  const handleEditSiteCultivatedArea = (site: ISite) => {
    openDialog({
      type: 'ADD_EDIT_CULTIVATED_AREA',
      props: {
        site,
      },
    });
  };

  const handleSearchTermChange = (searchTerm: string) => {
    logEvent('SITE_SEARCHED', { searchTerm });
    setQuery({ [appQueryParams.query]: searchTerm });
  };

  const handleSiteViewOptionChange = (siteViewOption: SiteViewOption) => {
    setQuery({ [appQueryParams.viewOption]: siteViewOption });
  };

  return {
    sites,
    partner,
    loading,
    error,
    searchTerm,
    hasNextPage,
    siteViewOption,
    refetch,
    handleSiteViewOptionChange,
    handleSearchTermChange,
    handleCreateSite,
    handleEditSite,
    handleDeleteSite,
    handleCreateSiteCluster,
    handleEditSiteCluster,
    handlePageEndReached,
    handleDeleteSiteCluster,
    handleManageClusterSites,
    handleEditSiteCultivatedArea: handleEditSiteCultivatedArea,
  };
};

export default usePartnerSites;
